import * as React from 'react';

import { faCheck, faX, faQuestionCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import { cva, type VariantProps } from 'class-variance-authority';

import {
    SectionBlock,
    SectionBlockBody,
    SectionBlockHeader,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { Separator } from 'src/registry/ui/Separator';
import { cn } from 'src/utils/shadcn';

function Wizard({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Root>) {
    return (
        <TabsPrimitive.Root
            data-slot="tabs"
            className={cn('flex h-full flex-1 flex-col gap-2', className)}
            {...props}
        />
    );
}

function WizardList({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.List>) {
    return (
        <TabsPrimitive.List
            data-slot="tabs-list"
            className={cn(
                'inline-flex h-12 w-fit items-center justify-center gap-1 rounded-full border p-[3px] shadow-xl',
                className,
            )}
            {...props}
        />
    );
}

const triggerVariants = cva(
    "group/trigger focus-visible:ring-primary-600 relative inline-flex w-auto shrink-0 items-center justify-center rounded-full bg-white whitespace-nowrap ring-offset-white transition focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-hidden [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-4",
    {
        variants: {
            variant: {
                default:
                    'data-[state=active]:text-primary-700 cursor-pointer gap-4 font-medium disabled:cursor-not-allowed disabled:text-gray-400 data-[state=active]:cursor-default',
                header: 'data-[state=active]:from-primary-500 data-[state=active]:to-primary-900 h-full flex-1 gap-1.5 px-2 py-1 font-medium hover:bg-gray-100 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-linear-to-br data-[state=active]:text-gray-50 data-[state=active]:shadow-sm',
            },
        },
        defaultVariants: {
            variant: 'default',
        },
    },
);

function WizardTrigger({
    className,
    variant,
    ...props
}: React.ComponentProps<typeof TabsPrimitive.Trigger> & VariantProps<typeof triggerVariants>) {
    return (
        <TabsPrimitive.Trigger
            data-slot="tabs-trigger"
            className={cn(triggerVariants({ variant }), className)}
            {...props}
        />
    );
}

function WizardTriggerNode({ className, children, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            className={cn(
                'group/node',
                // Completed
                'bg-primary-600 flex size-8 shrink-0 items-center justify-center rounded-full border-1 border-transparent text-white transition group-hover/trigger:scale-110',
                // Active
                'group-data-[state=active]/trigger:border-primary-600 group-data-[state=active]/trigger:text-primary-600 group-data-[state=active]/trigger:bg-primary-50',
                // Disabled
                'group-disabled/trigger:border-gray-300 group-disabled/trigger:bg-white group-disabled/trigger:text-gray-400',
                // Error
                'aria-invalid:border-red-600 aria-invalid:bg-red-600 aria-invalid:text-white',
                className,
            )}
            {...props}
        >
            <FontAwesomeIcon
                className="group-disabled/trigger:hidden! group-aria-invalid/node:hidden! group-data-[state=active]/trigger:hidden!"
                icon={faCheck}
            />
            <FontAwesomeIcon className="hidden! group-aria-invalid/node:block!" icon={faX} />
            <span className="hidden text-xs font-semibold group-disabled/trigger:block group-aria-invalid/node:hidden! group-data-[state=active]/trigger:block">
                {children}
            </span>
        </span>
    );
}

function WizardTriggerMessage({ className, children, ...props }: React.ComponentProps<'span'>) {
    return (
        <span
            className={cn(
                // Completed
                'hidden bg-white whitespace-nowrap text-black group-disabled/trigger:text-gray-400 md:block',
                // Disabled
                'group-disabled/trigger:text-gray-400',
            )}
            {...props}
        >
            {children}
        </span>
    );
}

function WizardContent({ className, ...props }: React.ComponentProps<typeof TabsPrimitive.Content>) {
    return (
        <TabsPrimitive.Content
            data-slot="tabs-content"
            className={cn('flex flex-1 flex-col gap-4 outline-none', className)}
            {...props}
        />
    );
}

type WizardStepInfoProps = {
    header: string;
    body: string;
    tip?: string;
};

function WizardStepInfo({ header, tip, body }: WizardStepInfoProps) {
    return (
        <div className="flex flex-col gap-2 rounded-xl bg-gray-100 p-5">
            <span className="font-bold text-black">{header}</span>
            <span className="text-sm text-gray-500">{body}</span>
            {tip && (
                <>
                    <Separator />
                    <div className="flex gap-2">
                        <span className="text-primary-500">
                            <FontAwesomeIcon icon={faQuestionCircle} />
                        </span>
                        <span className="text-sm text-gray-500">{tip}</span>
                    </div>
                </>
            )}
        </div>
    );
}

type WizardOverviewSectionProps = {
    title: string;
    data: { title: string; value?: React.ReactNode | null }[];
};

export default function WizardOverviewSection({ title, data }: WizardOverviewSectionProps) {
    return (
        <SectionBlock className="mb-4 flex w-full break-inside-avoid flex-col gap-2 sm:gap-2">
            <SectionBlockHeader>
                <SectionBlockTitle className="flex w-full items-center justify-between whitespace-nowrap">
                    <div className="flex items-center gap-2">{title}</div>
                </SectionBlockTitle>
            </SectionBlockHeader>
            <Separator />
            <SectionBlockBody className="flex flex-col gap-2 sm:gap-2">
                {data.map(({ title, value }) => (
                    <React.Fragment key={title}>
                        <div className="flex justify-between gap-x-4 px-2 lg:flex-row lg:break-normal">
                            <div className="font-medium break-words text-gray-500 lg:w-1/2">{title}</div>
                            <div className="break-words lg:max-w-full lg:truncate">{value ?? '-'}</div>
                        </div>
                        <Separator />
                    </React.Fragment>
                ))}
            </SectionBlockBody>
        </SectionBlock>
    );
}

export {
    Wizard,
    WizardList,
    WizardTrigger,
    WizardTriggerNode,
    WizardContent,
    WizardTriggerMessage,
    WizardStepInfo,
    WizardOverviewSection,
};
